<template>
  <div class="progress-shell">
    <div
      class="progress-track"
      role="progressbar"
      :aria-label="progressLabel"
      :aria-valuenow="clampedValue"
      aria-valuemin="0"
      :aria-valuemax="safeMax">
      <div
        class="progress-fill"
        :style="fillStyle"
        @transitionend="handleFillTransitionEnd">
      </div>
    </div>

    <div v-if="showLabel" class="progress-label">
      {{ Math.round(percentage) }}%
    </div>

    <Teleport to="body">
      <div v-if="shouldShowConfetti" class="pointer-events-none fixed inset-0 z-[9999] h-screen w-screen overflow-hidden">
        <ConfettiAnimation />
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import ConfettiAnimation from './ConfettiAnimation.vue'

const props = defineProps({
  value: {
    type: Number,
    required: true,
  },
  max: {
    type: Number,
    default: 100,
  },
  duration: {
    type: Number,
    default: 5000,
  },
  color: {
    type: String,
    default: '#16a34a',
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  showConfetti: {
    type: Boolean,
    default: false,
  },
  celebrate: {
    type: Boolean,
    default: false,
  },
  fillDuration: {
    type: Number,
    default: 1150,
  },
})

const prefersReducedMotion = ref(false)
const shouldShowConfetti = ref(false)
const displayedPercentage = ref(0)
const hasCelebratedCompletion = ref(false)
const isMounted = ref(false)
let reducedMotionQuery
let confettiTimeout
let fillTimeout

const safeMax = computed(() => Math.max(0, Number(props.max) || 0))
const clampedValue = computed(() => Math.min(safeMax.value, Math.max(0, Number(props.value) || 0)))
const percentage = computed(() => (safeMax.value === 0 ? 0 : (clampedValue.value / safeMax.value) * 100))
const progressLabel = computed(() => props.label || 'Progress')

const fillStyle = computed(() => ({
  width: `${Math.max(0, Math.min(displayedPercentage.value, 100))}%`,
  background: `linear-gradient(90deg, ${props.color}, ${props.color})`,
  boxShadow: `0 8px 22px -12px ${props.color}b3`,
  '--progress-fill-duration': `${Math.max(0, props.fillDuration)}ms`,
}))

const isComplete = computed(() => percentage.value >= 100)

const canCelebrateCompletion = computed(() => {
  if (prefersReducedMotion.value) {
    return false
  }

  return isComplete.value && (props.showConfetti || props.celebrate)
})

const clearConfettiTimeout = () => {
  if (confettiTimeout) {
    clearTimeout(confettiTimeout)
    confettiTimeout = null
  }
}

const clearFillTimeout = () => {
  if (fillTimeout) {
    clearTimeout(fillTimeout)
    fillTimeout = null
  }
}

const updateReducedMotion = (event) => {
  prefersReducedMotion.value = event.matches
}

const showConfettiForDuration = () => {
  clearConfettiTimeout()
  hasCelebratedCompletion.value = true
  shouldShowConfetti.value = true
  confettiTimeout = setTimeout(() => {
    shouldShowConfetti.value = false
    confettiTimeout = null
  }, Math.max(0, props.duration))
}

const handleFillTransitionEnd = (event) => {
  if (event.propertyName !== 'width' || !canCelebrateCompletion.value || hasCelebratedCompletion.value) {
    return
  }

  showConfettiForDuration()
}

watch(percentage, (newPercentage) => {
  if (newPercentage < 100) {
    hasCelebratedCompletion.value = false
  }

  if (isMounted.value) {
    clearFillTimeout()
    displayedPercentage.value = 0
    fillTimeout = setTimeout(() => {
      displayedPercentage.value = newPercentage
      fillTimeout = null
    }, 36)
  }
})

watch(canCelebrateCompletion, (canCelebrate) => {
  if (!canCelebrate) {
    clearConfettiTimeout()
    shouldShowConfetti.value = false
  }
})

onMounted(() => {
  isMounted.value = true
  reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  prefersReducedMotion.value = reducedMotionQuery.matches
  reducedMotionQuery.addEventListener('change', updateReducedMotion)

  fillTimeout = setTimeout(() => {
    displayedPercentage.value = percentage.value
    fillTimeout = null
  }, 50)
})

onUnmounted(() => {
  clearConfettiTimeout()
  clearFillTimeout()
  reducedMotionQuery?.removeEventListener('change', updateReducedMotion)
})
</script>

<style scoped>
.progress-shell {
  width: 100%;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 14px;
  overflow: hidden;
  border-radius: 999px;
  background: linear-gradient(180deg, #eef2f7 0%, #e2e8f0 100%);
  box-shadow:
    inset 0 1px 1px rgba(255, 255, 255, 0.82),
    inset 0 -1px 2px rgba(15, 23, 42, 0.06);
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  transition: width var(--progress-fill-duration, 1150ms) cubic-bezier(0.2, 0.9, 0.22, 1);
}

.progress-label {
  margin-top: 8px;
  text-align: right;
  font-size: 0.74rem;
  line-height: 1rem;
  font-weight: 700;
  color: #475569;
}

@media (prefers-reduced-motion: reduce) {
  .progress-fill {
    transition: none;
  }
}
</style>
